import { useNavigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { fetchModuleMappings, hasSubModulePermission, isAdmin } from '../../utils/permissions'
import formRegistry from '../PatientServices/Forms/formRegistry'
import { setBusinessSettings } from '../../store/businessSlice'
import axiosInstance from '../../api/axiosInstance'
import { getCurrentUserId } from '../../utils/functions'

const COLORS = { gray: '#5B6B79', blue: '#96C5F7', green: '#2CA87F', red: '#9E2B25' }

const NavCard = ({ icon, iconClass, title, disabled, onClick }) => (
  <div
    onClick={disabled ? undefined : onClick}
    style={{
      background: '#fff',
      border: `1px solid ${disabled ? '#f0f2f5' : '#e8ecf0'}`,
      borderRadius: 12,
      padding: '20px 16px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 12,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.45 : 1,
      transition: 'box-shadow 0.15s, border-color 0.15s, transform 0.1s',
      userSelect: 'none',
    }}
    onMouseEnter={e => {
      if (!disabled) {
        e.currentTarget.style.boxShadow = '0 4px 16px rgba(150,197,247,0.25)'
        e.currentTarget.style.borderColor = COLORS.blue
        e.currentTarget.style.transform = 'translateY(-2px)'
      }
    }}
    onMouseLeave={e => {
      if (!disabled) {
        e.currentTarget.style.boxShadow = 'none'
        e.currentTarget.style.borderColor = '#e8ecf0'
        e.currentTarget.style.transform = 'translateY(0)'
      }
    }}
  >
    <div style={{ width: 48, height: 48, borderRadius: 12, background: disabled ? '#f0f2f5' : `${COLORS.blue}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <i className={iconClass || `ti ${icon}`} style={{ fontSize: 22, color: disabled ? '#bbb' : COLORS.blue }} />
    </div>
    <span style={{ fontSize: 13, fontWeight: 600, color: disabled ? '#aaa' : '#1a202c', textAlign: 'center', lineHeight: 1.3 }}>{title}</span>
  </div>
)

const Medical = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const [showFormsModal, setShowFormsModal] = useState(false)
  const [formToggles, setFormToggles] = useState({})
  const [savingForms, setSavingForms] = useState(false)
  const [consentTemplates, setConsentTemplates] = useState([])
  const [consentToggles, setConsentToggles] = useState({})
  const [customFormTemplates, setCustomFormTemplates] = useState([])
  const [customFormToggles, setCustomFormToggles] = useState({})

  const openFormsModal = async () => {
    const stored = JSON.parse(localStorage.getItem('businessSetting') || '{}')

    const enabled = stored?.enabled_forms
    const initial = {}
    formRegistry.forEach(f => {
      initial[f.value] = enabled === null || enabled === undefined ? true : enabled.includes(f.value)
    })
    setFormToggles(initial)

    try {
      const res = await axiosInstance.get('consentTemplates')
      const templates = res.data?.data || []
      setConsentTemplates(templates)
      const enabledConsents = stored?.enabled_consents
      const consentInit = {}
      templates.forEach(t => {
        consentInit[t.id] = enabledConsents === null || enabledConsents === undefined ? true : enabledConsents.includes(t.id)
      })
      setConsentToggles(consentInit)
    } catch (err) {
      console.error('Failed to load consent templates:', err)
      setConsentTemplates([])
    }

    try {
      const res = await axiosInstance.get('customFormTemplates')
      const templates = res.data?.data || []
      setCustomFormTemplates(templates)
      const enabledCustomForms = stored?.enabled_custom_forms
      const customFormInit = {}
      templates.forEach(t => {
        customFormInit[t.id] = enabledCustomForms === null || enabledCustomForms === undefined ? true : enabledCustomForms.includes(t.id)
      })
      setCustomFormToggles(customFormInit)
    } catch (err) {
      console.error('Failed to load custom form templates:', err)
      setCustomFormTemplates([])
    }

    setShowFormsModal(true)
  }

  const handleSaveForms = async () => {
    setSavingForms(true)
    try {
      const enabledList = formRegistry.filter(f => formToggles[f.value]).map(f => f.value)
      const enabledConsents = consentTemplates.filter(t => consentToggles[t.id]).map(t => t.id)
      const enabledCustomForms = customFormTemplates.filter(t => customFormToggles[t.id]).map(t => t.id)
      const doctorId = getCurrentUserId()
      await axiosInstance.put(`updateBusinessSetting/${doctorId}`, { enabled_forms: enabledList, enabled_consents: enabledConsents, enabled_custom_forms: enabledCustomForms })
      const stored = JSON.parse(localStorage.getItem('businessSetting') || '{}')
      const updated = { ...stored, enabled_forms: enabledList, enabled_consents: enabledConsents, enabled_custom_forms: enabledCustomForms }
      localStorage.setItem('businessSetting', JSON.stringify(updated))
      dispatch(setBusinessSettings(updated))
      setShowFormsModal(false)
    } catch (err) {
      console.error('Failed to save enabled forms:', err)
    } finally {
      setSavingForms(false)
    }
  }

  const [permissions, setPermissions] = useState({
    icd10List: false, diagnosisList: false, serviceCategory: false, allergy: false,
    chronicIllness: false, chiefComplaint: false, historyOfPastIllness: false,
    historyOfPresentIllness: false, examinationGeneral: false, examinationLocal: false,
    examinationSystemic: false, advises: false, extraNotes: false, treatmentPlan: false,
    familyHistory: false, psychosocial: false, labTests: false,
  })

  useEffect(() => {
    const checkPermissions = async () => {
      if (isAdmin()) {
        setPermissions({
          icd10List: true, diagnosisList: true, serviceCategory: true, allergy: true,
          chronicIllness: true, chiefComplaint: true, historyOfPastIllness: true,
          historyOfPresentIllness: true, examinationGeneral: true, examinationLocal: true,
          examinationSystemic: true, advises: true, extraNotes: true, treatmentPlan: true,
          familyHistory: true, psychosocial: true, labTests: true,
        })
        return
      }
      try {
        const allModules = await fetchModuleMappings()
        const medicalModule = allModules.find(m => m.module_name.toLowerCase() === 'medicals')
        if (medicalModule?.sub_modules) {
          const sm = medicalModule.sub_modules
          const p = name => hasSubModulePermission(sm.find(s => s.sub_module_name === name)?.id)
          setPermissions({
            icd10List: p('icd_10_list'), diagnosisList: p('diagnosis_list'),
            serviceCategory: p('service_category'), allergy: p('allergy'),
            chronicIllness: p('chronic_illness'), chiefComplaint: p('chief_complaint'),
            historyOfPastIllness: p('history_of_past_illness'),
            historyOfPresentIllness: p('history_of_present_illness'),
            examinationGeneral: p('examination_general'), examinationLocal: p('examination_local'),
            examinationSystemic: p('examination_systematic'),
            advises: p('advises'), extraNotes: p('extra_notes'), treatmentPlan: p('treatment_plan'),
            familyHistory: p('family_history'), psychosocial: p('psychosocial'), labTests: true,
          })
        }
      } catch (err) {
        console.error('Error checking permissions', err)
      }
    }
    checkPermissions()
  }, [])

  const cards = [
    { icon: 'ti-list-numbers', title: 'ICD 10 List', disabled: !permissions.icd10List, onClick: () => navigate('/Admin/ICDlist') },
    { icon: 'ti-stethoscope', title: 'Diagnosis List', disabled: !permissions.diagnosisList, onClick: () => navigate('/Admin/ICDstandardlist') },
    { icon: 'ti-category', title: 'Service Category', disabled: !permissions.serviceCategory, onClick: () => navigate('/Admin/ServiceCategory') },
    { icon: 'ti-shield-exclamation', title: 'Allergy', disabled: !permissions.allergy, onClick: () => navigate('/Admin/Allergy') },
    { icon: 'ti-heart-broken', title: 'Chronic Illness', disabled: !permissions.chronicIllness, onClick: () => navigate('/Admin/Chronicillness') },
    { icon: 'ti-message-circle', title: 'Chief Complaint', disabled: !permissions.chiefComplaint, onClick: () => navigate('/Admin/chiefComplaint') },
    { icon: 'ti-clock', title: 'History of Past Illness', disabled: !permissions.historyOfPastIllness, onClick: () => navigate('/Admin/historyodpastillenss') },
    { icon: 'ti-notes', title: 'History of Present Illness', disabled: !permissions.historyOfPresentIllness, onClick: () => navigate('/Admin/historyodpresentillness') },
    { icon: 'ti-search', title: 'Examination General', disabled: !permissions.examinationGeneral, onClick: () => navigate('/Admin/examinationgeneral') },
    { icon: 'ti-zoom-in', title: 'Examination Local', disabled: !permissions.examinationLocal, onClick: () => navigate('/Admin/examinationlocal') },
    { icon: 'ti-activity', title: 'Examination Systemic', disabled: !permissions.examinationSystemic, onClick: () => navigate('/Admin/examinationsymetic') },
    { icon: 'ti-info-circle', title: 'Advises', disabled: !permissions.advises, onClick: () => navigate('/Admin/Advice') },
    { icon: 'ti-file-description', title: 'Extra Notes', disabled: !permissions.extraNotes, onClick: () => navigate('/Admin/Extranotes') },
    { icon: 'ti-clipboard-heart', title: 'Treatment Plan', disabled: !permissions.treatmentPlan, onClick: () => navigate('/Admin/treatmentplan') },
    { icon: 'ti-users', title: 'Family History', disabled: !permissions.familyHistory, onClick: () => navigate('/Admin/FamilyHistory') },
    { icon: 'ti-brain', title: 'Psychosocial', disabled: !permissions.psychosocial, onClick: () => navigate('/Admin/Physocial') },
    { icon: '', iconClass: 'fas fa-tooth', title: 'Dental Notes', disabled: false, onClick: () => navigate('/Admin/DentalNoteTemplates') },
    { icon: 'ti-flask', title: 'Lab Tests', disabled: !permissions.labTests, onClick: () => navigate('/Admin/LabTests') },
    { icon: 'ti-file-text', title: 'Medical Templates - Notes', disabled: false, onClick: () => navigate('/Admin/MedicalTemplates/MedicalNotes') },
    { icon: 'ti-prescription', title: 'Medical Templates - Prescriptions', disabled: false, onClick: () => navigate('/Admin/MedicalTemplates/Perscriptions') },
    { icon: 'ti-forms', title: 'Patient Forms', disabled: false, onClick: openFormsModal },
    { icon: 'ti-writing', title: 'Consents', disabled: false, onClick: () => navigate('/Admin/Consents') },
    { icon: 'ti-writing', title: 'Custom Forms', disabled: false, onClick: () => navigate('/Admin/CustomForms') },
    { icon: 'ti-calendar-time', title: 'Schedules', disabled: false, onClick: () => navigate('/Admin/Schedules') },
    { icon: 'ti-alert-circle', title: 'Postcare', disabled: false, onClick: () => navigate('/Admin/Postcare') },
    { icon: 'ti-upload', title: 'Upload Types', disabled: false, onClick: () => navigate('/Admin/UploadTypes') },
  ]

  return (
    <div className="pc-container">
      <div className="pc-content">
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e8ecf0', overflow: 'hidden' }}>
          <div style={{ padding: '16px 24px', borderBottom: '1px solid #e8ecf0', display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: `${COLORS.blue}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="ti ti-medical-cross" style={{ color: COLORS.blue, fontSize: 19 }} />
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#1a202c' }}>Medicals</div>
              <div style={{ fontSize: 11, color: COLORS.gray }}>Manage all medical settings and templates</div>
            </div>
          </div>

          <div style={{ padding: 24 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 16 }}>
              {cards.map((card, i) => (
                <NavCard key={i} icon={card.icon} iconClass={card.iconClass} title={card.title} disabled={card.disabled} onClick={card.onClick} />
              ))}
            </div>
          </div>
        </div>
      </div>

      {showFormsModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1050, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: 40, overflowY: 'auto' }} onClick={() => setShowFormsModal(false)}>
          <div style={{ background: '#fff', borderRadius: 12, width: '90%', maxWidth: 520, boxShadow: '0 8px 32px rgba(0,0,0,0.18)', marginBottom: 40 }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '18px 24px', borderBottom: '1px solid #e8ecf0' }}>
              <div style={{ width: 34, height: 34, borderRadius: 8, background: `${COLORS.blue}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className="ti ti-forms" style={{ color: COLORS.blue, fontSize: 18 }} />
              </div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1a202c' }}>Patient Forms</div>
                <div style={{ fontSize: 11, color: COLORS.gray }}>Toggle forms and consent templates</div>
              </div>
              <button onClick={() => setShowFormsModal(false)} style={{ marginLeft: 'auto', width: 26, height: 26, borderRadius: 6, border: '1px solid #e8ecf0', background: '#f8f9fb', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: COLORS.gray, fontSize: 14 }}>
                <i className="ti ti-x" />
              </button>
            </div>

            <div style={{ padding: '20px 24px', maxHeight: '60vh', overflowY: 'auto' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.gray, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Patient Forms</div>
              <div style={{ fontSize: 12, color: COLORS.gray, marginBottom: 14 }}>Toggle which forms appear in the "Select Form Type" dropdown for patients.</div>
              {formRegistry.map(form => (
                <label key={form.value} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #f0f2f5', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={!!formToggles[form.value]}
                    onChange={e => setFormToggles(prev => ({ ...prev, [form.value]: e.target.checked }))}
                    style={{ width: 15, height: 15, accentColor: COLORS.blue, cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: 13, color: '#1a202c' }}>{form.label}</span>
                </label>
              ))}

              <div style={{ margin: '18px 0 6px', borderTop: '1px solid #e8ecf0', paddingTop: 18 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.gray, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Consents</div>
                <div style={{ fontSize: 12, color: COLORS.gray, marginBottom: 14 }}>Toggle which consent templates are available for patients.</div>
              </div>
              {consentTemplates.length === 0 ? (
                <div style={{ fontSize: 13, color: COLORS.gray, fontStyle: 'italic' }}>
                  No consent templates found.{' '}
                  <span style={{ color: COLORS.blue, cursor: 'pointer', textDecoration: 'underline' }} onClick={() => { setShowFormsModal(false); navigate('/Admin/Consents') }}>Add one</span>.
                </div>
              ) : (
                consentTemplates.map(t => (
                  <label key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #f0f2f5', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={!!consentToggles[t.id]}
                      onChange={e => setConsentToggles(prev => ({ ...prev, [t.id]: e.target.checked }))}
                      style={{ width: 15, height: 15, accentColor: COLORS.blue, cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: 13, color: '#1a202c' }}>{t.name}</span>
                  </label>
                ))
              )}

              <div style={{ margin: '18px 0 6px', borderTop: '1px solid #e8ecf0', paddingTop: 18 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.gray, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>Custom Forms</div>
                <div style={{ fontSize: 12, color: COLORS.gray, marginBottom: 14 }}>Toggle which custom form templates are available for patients.</div>
              </div>
              {customFormTemplates.length === 0 ? (
                <div style={{ fontSize: 13, color: COLORS.gray, fontStyle: 'italic' }}>
                  No custom form templates found.{' '}
                  <span style={{ color: COLORS.blue, cursor: 'pointer', textDecoration: 'underline' }} onClick={() => { setShowFormsModal(false); navigate('/Admin/CustomForms') }}>Add one</span>.
                </div>
              ) : (
                customFormTemplates.map(t => (
                  <label key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #f0f2f5', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={!!customFormToggles[t.id]}
                      onChange={e => setCustomFormToggles(prev => ({ ...prev, [t.id]: e.target.checked }))}
                      style={{ width: 15, height: 15, accentColor: COLORS.blue, cursor: 'pointer' }}
                    />
                    <span style={{ fontSize: 13, color: '#1a202c' }}>{t.name}</span>
                  </label>
                ))
              )}
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', padding: '14px 24px', borderTop: '1px solid #e8ecf0' }}>
              <button onClick={() => setShowFormsModal(false)} style={{ padding: '7px 16px', fontSize: 13, borderRadius: 8, border: '1px solid #dde3eb', background: '#f8f9fb', color: COLORS.gray, cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleSaveForms} disabled={savingForms} className="bgBtn btn-sm d-flex align-items-center gap-1" style={{ padding: '7px 18px', fontSize: 13 }}>
                {savingForms && <i className="ti ti-loader-2" style={{ fontSize: 14 }} />}{savingForms ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Medical
